// DEFCON TERMINAL - Main Application Entry Point
// A Cold War text-based strategic simulation game

import { Terminal } from './terminal';
import { GameEngine } from './engine';
import { WarDialer, type DiscoveredSystem } from './wardialer';
import { AsciiRenderer } from './ascii';
import { AudioManager } from './audio';
import { sessionManager, apiClient } from './services';
import { getScenarioById, getScenarioSummaries } from './scenarios';
import { HIDDEN_COMMANDS } from '@shared/constants';
import type { PathType, EndingNode, TimedNode, MultiSelectNode } from '@shared/types';
import type { LaunchDecisionResult } from './sequences/NuclearLaunchSequences';

// Application state
interface AppState {
  phase: 'boot' | 'title' | 'login' | 'menu' | 'wardialer' | 'connecting' | 'playing' | 'ending' | 'leaderboard';
  terminal: Terminal | null;
  engine: GameEngine | null;
  warDialer: WarDialer | null;
  ascii: AsciiRenderer | null;
  audio: AudioManager | null;
  selectedPath: PathType | null;
}

const state: AppState = {
  phase: 'boot',
  terminal: null,
  engine: null,
  warDialer: null,
  ascii: null,
  audio: null,
  selectedPath: null,
};

// Boot sequence
async function bootSequence(): Promise<void> {
  const terminal = state.terminal!;

  terminal.clear();

  await terminal.printWithDelay('DEFCON TERMINAL v1.0', 20);
  await terminal.println('');
  await delay(300);

  await terminal.printWithDelay('> INITIALIZING SYSTEM...', 25);
  await delay(400);
  await terminal.printWithDelay('> MEMORY CHECK: 640K OK', 25);
  await delay(250);
  await terminal.printWithDelay('> LOADING TERMINAL DRIVER...', 25);
  await delay(300);
  await terminal.printWithDelay('> MODEM INITIALIZED: 2400 BAUD', 25);
  await delay(250);

  const audioStatus = state.audio?.isEnabled() ? 'READY' : 'STANDBY';
  await terminal.printWithDelay(`> AUDIO SUBSYSTEM: ${audioStatus}`, 25);
  await delay(400);

  await terminal.println('');
  await terminal.printWithDelay('> SYSTEM READY.', 25);
  await delay(800);
}

// Title screen
async function showTitleScreen(): Promise<void> {
  const terminal = state.terminal!;

  terminal.clear();

  // Simple ASCII title that works reliably with monospace fonts
  const titleArt = [
    '',
    '',
    '    ____  ______ ______ ____ ____  _   __',
    '   / __ \\/ ____// ____// __ ) __ \\/ | / /',
    '  / / / / __/  / /_   / /  / / / /  |/ / ',
    ' / /_/ / /___ / __/  / /__/ /_/ / /|  /  ',
    '/_____/_____//_/     \\___/\\____/_/ |_/   ',
    '',
    '            T E R M I N A L',
    '',
    '            [ JANUARY 1984 ]',
    '',
    '   ----------------------------------------',
    '',
    '     NORTH AMERICAN AEROSPACE DEFENSE',
    '          COMMAND INTEGRATION',
    '',
    '   ----------------------------------------',
    '',
    '         [ PRESS ANY KEY TO BEGIN ]',
    '',
    '',
  ];

  for (const line of titleArt) {
    terminal.println(line);
  }

  // Wait for any keypress
  await terminal.waitForKey();

  // Initialize audio on first interaction (browser requirement)
  if (state.audio) {
    try {
      await state.audio.initialize();
    } catch (e) {
      console.warn('Audio initialization failed:', e);
    }
  }
}

// Login/registration screen
async function showLoginScreen(): Promise<void> {
  const terminal = state.terminal!;

  terminal.clear();

  terminal.println('╔════════════════════════════════════════════════════════════╗');
  terminal.println('║              OPERATOR IDENTIFICATION                       ║');
  terminal.println('╚════════════════════════════════════════════════════════════╝');
  terminal.println('');

  // Check for existing session
  const existingId = sessionManager.getStoredOperatorId();
  const existingHandle = sessionManager.getStoredHandle();

  if (existingId && existingHandle) {
    terminal.println(`  Previous session detected: ${existingHandle}`);
    terminal.println('');
    terminal.println('  [1] Continue as ' + existingHandle);
    terminal.println('  [2] New Operator');
    terminal.println('  [3] Enter Different Operator ID');
    terminal.println('');

    const choice = await terminal.prompt('  SELECT OPTION [1-3]: ');

    if (choice === '1') {
      try {
        terminal.println('');
        await terminal.printWithDelay('  Restoring session...', 25);
        await sessionManager.loadExistingSession(existingId);
        terminal.println(' OK');
        await delay(500);
        return;
      } catch (error) {
        terminal.println(' FAILED');
        terminal.println('  Session expired. Creating new operator.');
        await delay(1000);
      }
    } else if (choice === '3') {
      await handleOperatorIdEntry();
      return;
    }
    // Fall through to new operator for choice '2' or failed restore
  }

  // New operator registration
  terminal.println('');
  terminal.println('  ═══════════════════════════════════════');
  terminal.println('            CREATE NEW OPERATOR');
  terminal.println('  ═══════════════════════════════════════');
  terminal.println('');
  terminal.println('  Enter your handle (3-20 characters):');
  terminal.println('  Allowed: letters, numbers, underscore');
  terminal.println('');

  let handle = '';
  let attempts = 0;
  const maxAttempts = 5;

  while (!handle && attempts < maxAttempts) {
    attempts++;
    const input = await terminal.prompt('  HANDLE> ');

    if (!input || input.trim().length === 0) {
      terminal.println('  ERROR: Handle cannot be empty.');
      continue;
    }

    if (!/^[a-zA-Z0-9_]{3,20}$/.test(input.trim())) {
      terminal.println('  ERROR: Invalid handle format.');
      terminal.println('  Use 3-20 alphanumeric characters or underscore.');
      continue;
    }

    handle = input.trim();
  }

  if (!handle) {
    terminal.println('  Too many invalid attempts. Using default handle.');
    handle = 'OPERATOR_' + Math.floor(Math.random() * 9999).toString().padStart(4, '0');
  }

  terminal.println('');
  await terminal.printWithDelay('  Creating operator profile...', 25);

  let session: { operatorId: string; handle?: string };
  let isOffline = false;

  try {
    // Attempt to create session via backend API
    session = await sessionManager.createNewSession(handle);
  } catch (error) {
    // API unreachable — fall back to offline/mock session
    console.warn('API unavailable, falling back to offline mode:', error);
    const mockId = generateMockOperatorId();
    sessionManager.setMockSession(handle, mockId);
    session = { operatorId: mockId };
    isOffline = true;
  }

  terminal.println(' OK');
  terminal.println('');
  terminal.println('  ╔══════════════════════════════════════════════════════╗');
  terminal.println('  ║        OPERATOR CREATED SUCCESSFULLY                 ║');
  terminal.println('  ╠══════════════════════════════════════════════════════╣');
  terminal.println(`  ║  Handle: ${handle.padEnd(41)}║`);
  terminal.println(`  ║  ID: ${session.operatorId.padEnd(45)}║`);
  terminal.println('  ╠══════════════════════════════════════════════════════╣');
  terminal.println('  ║  SAVE YOUR ID TO RESTORE SESSION LATER               ║');
  terminal.println('  ╚══════════════════════════════════════════════════════╝');
  terminal.println('');

  if (isOffline) {
    terminal.println('  \\c[yellow]OFFLINE MODE — scores will not sync to server\\c[reset]');
    terminal.println('');
  }

  // Copy operator ID to clipboard
  try {
    await navigator.clipboard.writeText(session.operatorId);
    terminal.println('  \\c[bright]Operator ID copied to clipboard!\\c[reset]');
  } catch {
    terminal.println('  \\c[dim]Press [C] to copy Operator ID to clipboard\\c[reset]');
  }
  terminal.println('');
  terminal.println('  Press [C] to copy ID, or any other key to continue...');

  const key = await terminal.waitForKey();
  if (key.toLowerCase() === 'c') {
    try {
      await navigator.clipboard.writeText(session.operatorId);
      terminal.println('');
      terminal.println('  \\c[bright]Operator ID copied!\\c[reset]');
      await delay(1000);
    } catch {
      terminal.println('');
      terminal.println('  \\c[red]Failed to copy. Your ID is: ' + session.operatorId + '\\c[reset]');
      await delay(2000);
    }
  }
}

// Generate a mock operator ID for offline play
function generateMockOperatorId(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let id = '';
  for (let i = 0; i < 8; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  id += '-';
  for (let i = 0; i < 4; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
}

// Tutorial screen
async function showTutorial(): Promise<void> {
  const terminal = state.terminal!;

  terminal.clear();

  terminal.println('');
  terminal.println('  ==========================================');
  terminal.println('             OPERATOR BRIEFING');
  terminal.println('  ==========================================');
  terminal.println('');

  await delay(500);

  await terminal.printWithDelay('  Welcome to DEFCON TERMINAL.', 25);
  terminal.println('');
  await terminal.printWithDelay('  The year is 1984. You have accessed', 25);
  await terminal.printWithDelay('  classified military computer systems.', 25);
  terminal.println('');

  await delay(500);

  terminal.println('  ------------------------------------------');
  terminal.println('  HOW TO PLAY:');
  terminal.println('  ------------------------------------------');
  terminal.println('');
  terminal.println('  1. Use the WAR DIALER to scan for systems');
  terminal.println('     and discover classified networks.');
  terminal.println('');
  terminal.println('  2. Each system presents different scenarios');
  terminal.println('     with branching decisions.');
  terminal.println('');
  terminal.println('  3. Your choices affect the world state:');
  terminal.println('     - DEFCON level (1=war, 5=peace)');
  terminal.println('     - Global tension');
  terminal.println('     - Casualty counts');
  terminal.println('');
  terminal.println('  4. Navigate menus by typing the number');
  terminal.println('     of your choice and pressing ENTER.');
  terminal.println('');
  terminal.println('  ------------------------------------------');
  terminal.println('  AVAILABLE SYSTEMS:');
  terminal.println('  ------------------------------------------');
  terminal.println('');
  terminal.println('  SARN-WEST    - US Strategic Command');
  terminal.println('  SARN-EAST    - Soviet Perimetr-2 System');
  terminal.println('  PHANTOM      - CIA Covert Operations');
  terminal.println('  RED SHADOW   - KGB Special Activities');
  terminal.println('');
  terminal.println('  ------------------------------------------');
  terminal.println('');
  terminal.println('  "The only winning move is not to play."');
  terminal.println('                         - WOPR, 1983');
  terminal.println('');
  terminal.println('  Press any key to continue...');

  await terminal.waitForKey();
}

async function handleOperatorIdEntry(): Promise<void> {
  const terminal = state.terminal!;

  await terminal.println('');
  await terminal.println('  Enter your Operator ID (format: XXXXXXXX-XXXX):');
  await terminal.println('');

  const operatorId = await terminal.prompt('  OPERATOR ID: ');

  if (!/^[A-Z0-9]{8}-[A-Z0-9]{4}$/i.test(operatorId)) {
    await terminal.println('  Invalid Operator ID format.');
    await delay(1500);
    await showLoginScreen();
    return;
  }

  await terminal.printWithDelay('  Validating...', 30);

  try {
    await sessionManager.loadExistingSession(operatorId.toUpperCase());
    await terminal.println(' OK');
    await terminal.println('  Session restored successfully.');
    await delay(1000);
  } catch (error) {
    await terminal.println(' FAILED');
    await terminal.println('  Operator ID not found or session expired.');
    await delay(2000);
    await showLoginScreen();
  }
}

// Main menu
async function showMainMenu(): Promise<void> {
  const terminal = state.terminal!;
  const session = sessionManager.getCurrentSession();

  if (!session) {
    await showLoginScreen();
    return;
  }

  await terminal.clear();
  await terminal.println('╔══════════════════════════════════════════════════════════════════╗');
  await terminal.println(`║  MAIN TERMINAL - OPERATOR: ${session.handle.toUpperCase().padEnd(36)}║`);
  await terminal.println(`║  ID: ${session.operatorId.padEnd(56)}║`);
  await terminal.println('╠══════════════════════════════════════════════════════════════════╣');
  await terminal.println('║                                                                  ║');
  await terminal.println('║  [1] RUN WAR DIALER                                              ║');
  await terminal.println('║  [2] VIEW OPERATOR RANKINGS                                      ║');
  await terminal.println('║  [3] REVIEW MISSION HISTORY                                      ║');
  await terminal.println('║  [4] SYSTEM CONFIGURATION                                        ║');
  await terminal.println('║  [5] DISCONNECT                                                  ║');
  await terminal.println('║                                                                  ║');
  await terminal.println('╚══════════════════════════════════════════════════════════════════╝');
  await terminal.println('');

  // Show stats
  const stats = session.statistics;
  await terminal.println(`  Scenarios Completed: ${stats.scenariosCompleted}  |  Highest Score: ${stats.highestScore}`);
  await terminal.println('');

  const choice = await terminal.prompt('  SELECT OPTION [1-5]: ');

  switch (choice) {
    case '1':
      state.phase = 'wardialer';
      await runWarDialer();
      break;
    case '2':
      state.phase = 'leaderboard';
      await showLeaderboard();
      break;
    case '3':
      await showMissionHistory();
      break;
    case '4':
      await showConfiguration();
      break;
    case '5':
      await disconnect();
      break;
    default:
      // Check for easter egg commands
      if (HIDDEN_COMMANDS.includes(choice.toUpperCase() as typeof HIDDEN_COMMANDS[number])) {
        await handleEasterEggCommand(choice.toUpperCase());
      } else {
        await terminal.println('  Invalid option.');
        await delay(1000);
      }
      await showMainMenu();
  }
}

// War Dialer
async function runWarDialer(): Promise<void> {
  const terminal = state.terminal!;
  const warDialer = state.warDialer!;

  await terminal.clear();

  // Reset war dialer state before starting
  warDialer.reset();

  // Display war dialer header
  await terminal.println('╔══════════════════════════════════════════════════════════════════╗');
  await terminal.println('║                    DEMON DIALER v2.3                             ║');
  await terminal.println('║                  "SCANNING THE VOID"                             ║');
  await terminal.println('╠══════════════════════════════════════════════════════════════════╣');
  await terminal.println('║                                                                  ║');
  await terminal.println('║  SCANNING PREFIX: 555-01XX                                       ║');
  await terminal.println('║                                                                  ║');
  await terminal.println('╚══════════════════════════════════════════════════════════════════╝');
  await terminal.println('');

  // Run the war dialer scan
  let discoveredSystems;
  try {
    discoveredSystems = await warDialer.startScan();
  } catch (error) {
    console.error('War dialer error:', error);
    await terminal.println('');
    await terminal.println('ERROR: WAR DIALER MALFUNCTION');
    await terminal.println('Press any key to return to menu...');
    await terminal.waitForKey();
    await showMainMenu();
    return;
  }

  await terminal.println('');
  await terminal.println('  SCAN COMPLETE. 4 SYSTEMS DISCOVERED.');
  await terminal.println('');
  await delay(1000);

  // Show discovered systems
  await terminal.println('╔══════════════════════════════════════════════════════════════════╗');
  await terminal.println('║                    DISCOVERED SYSTEMS                            ║');
  await terminal.println('╠══════════════════════════════════════════════════════════════════╣');

  for (let i = 0; i < discoveredSystems.length; i++) {
    const sys = discoveredSystems[i];
    await terminal.println(`║  [${i + 1}] ${sys.phoneNumber} - ${sys.description.padEnd(43)}║`);
  }

  await terminal.println('║                                                                  ║');
  await terminal.println('║  [0] RETURN TO MAIN MENU                                         ║');
  await terminal.println('╚══════════════════════════════════════════════════════════════════╝');
  await terminal.println('');

  const choice = await terminal.prompt('  SELECT SYSTEM TO CONNECT [0-4]: ');

  if (choice === '0') {
    state.phase = 'menu';
    await showMainMenu();
    return;
  }

  const systemIndex = parseInt(choice) - 1;
  if (systemIndex >= 0 && systemIndex < discoveredSystems.length) {
    state.selectedPath = discoveredSystems[systemIndex].systemType;
    state.phase = 'connecting';
    await connectToSystem(discoveredSystems[systemIndex]);
  } else {
    await terminal.println('  Invalid selection.');
    await delay(1000);
    await runWarDialer();
  }
}

async function connectToSystem(system: DiscoveredSystem): Promise<void> {
  const terminal = state.terminal!;
  const audio = state.audio;

  await terminal.clear();

  // Generate international phone number for this connection
  const generateIntlNumber = () => {
    const codes = ['1', '7', '33', '44', '49', '81', '86', '39', '358', '46', '47', '48', '420'];
    const cc = codes[Math.floor(Math.random() * codes.length)];
    let digits = '';
    for (let i = 0; i < 30; i++) digits += Math.floor(Math.random() * 10);
    const g1 = digits.slice(0, 3), g2 = digits.slice(3, 7), g3 = digits.slice(7, 11);
    const g4 = digits.slice(11, 19), g5 = digits.slice(19);
    return `+${cc}-${g1}-${g2}-${g3}-${g4}-${g5}`;
  };

  await terminal.println('╔══════════════════════════════════════════════════════════════════╗');
  await terminal.println('║                    ESTABLISHING CONNECTION                       ║');
  await terminal.println('╠══════════════════════════════════════════════════════════════════╣');

  // Play the full war dial sequence with synchronized text descriptions
  if (audio && audio.isReady()) {
    const sequenceResult = await audio.playWarDialSequence();

    if (sequenceResult) {
      // Show each phase with synchronized text
      for (const phase of sequenceResult.timeline) {
        await terminal.println(`║  ${phase.description.padEnd(64)}║`);
        // Wait for this phase to complete
        await delay(phase.duration * 900);
      }
    }
  } else {
    // Text-only mode for those without audio
    const phoneFormatted = generateIntlNumber();

    await terminal.println('║                                                                  ║');
    await terminal.println(`║  DIALING: ${phoneFormatted.padEnd(53)}║`);
    await delay(800);
    await terminal.println('║  WAITING FOR CARRIER...                                          ║');
    await delay(400);
    await terminal.println('║  2600Hz CARRIER TONE - TRUNK LINE SEIZED                         ║');
    await delay(500);
    await terminal.println('║  MF ROUTING: 24 INTERNATIONAL TRUNK TONES                        ║');
    await delay(700);
    await terminal.println('║  CONNECTING TO REMOTE SYSTEM...                                  ║');
    await delay(400);
    await terminal.println('║  RINGING... (2 RINGS)                                            ║');
    await delay(600);
    await terminal.println('║  MODEM ANSWERING - ESTABLISHING CONNECTION                       ║');
    await delay(500);
  }

  await terminal.println('║                                                                  ║');
  await terminal.println('║  CONNECTION ESTABLISHED                                          ║');
  await terminal.println('╚══════════════════════════════════════════════════════════════════╝');
  await terminal.println('');
  await delay(800);

  // Show system-specific banner
  await terminal.println('');

  switch (system.systemType) {
    case 'sarn-west':
      await terminal.println('═══════════════════════════════════════════════════════════════════');
      await terminal.println('          S.A.R.N. - STRATEGIC AUTOMATED RESPONSE NETWORK');
      await terminal.println('                    WESTERN THEATER COMMAND');
      await terminal.println('═══════════════════════════════════════════════════════════════════');
      await terminal.println('');
      await terminal.println('         WARNING: UNAUTHORIZED ACCESS IS A FEDERAL CRIME');
      await terminal.println('                   UNDER 18 U.S.C. § 1030');
      break;

    case 'sarn-east':
      await terminal.println('═══════════════════════════════════════════════════════════════════');
      await terminal.println('                    ПЕРИМЕТР-2 / PERIMETR-2');
      await terminal.println('           АВТОМАТИЗИРОВАННАЯ СИСТЕМА СТРАТЕГИЧЕСКОГО ОТВЕТА');
      await terminal.println('              AUTOMATED STRATEGIC RESPONSE SYSTEM');
      await terminal.println('═══════════════════════════════════════════════════════════════════');
      await terminal.println('');
      await terminal.println('         ПРЕДУПРЕЖДЕНИЕ: НЕСАНКЦИОНИРОВАННЫЙ ДОСТУП ЗАПРЕЩЁН');
      await terminal.println('         WARNING: UNAUTHORIZED ACCESS PROHIBITED');
      await terminal.println('');
      await terminal.println('         TRANSLATION MODE: [ENABLED]');
      break;

    case 'phantom-directive':
      await terminal.println('═══════════════════════════════════════════════════════════════════');
      await terminal.println('                    PHANTOM DIRECTIVE');
      await terminal.println('              SPECIAL ACTIVITIES COORDINATION CENTER');
      await terminal.println('═══════════════════════════════════════════════════════════════════');
      await terminal.println('');
      await terminal.println('         CLASSIFICATION: TOP SECRET / SPECIAL ACCESS REQUIRED');
      await terminal.println('');
      await terminal.println('         BIOMETRIC BYPASS: [ACTIVE]');
      await terminal.println('         VOICEPRINT: [SPOOFED]');
      break;

    case 'red-shadow':
      await terminal.println('═══════════════════════════════════════════════════════════════════');
      await terminal.println('                    КРАСНАЯ ТЕНЬ / RED SHADOW');
      await terminal.println('                 DIRECTORATE S - SPECIAL OPERATIONS');
      await terminal.println('═══════════════════════════════════════════════════════════════════');
      await terminal.println('');
      await terminal.println('         СЕКРЕТНО / CLASSIFIED');
      await terminal.println('');
      await terminal.println('         IDENTITY VERIFICATION: [BYPASSED]');
      break;
  }

  await terminal.println('');
  await delay(2000);

  // Start the game engine with this path
  state.phase = 'playing';
  await startGame(system.systemType);
}

async function startGame(path: PathType): Promise<void> {
  const terminal = state.terminal!;
  const engine = state.engine!;

  // Load available scenarios for this path
  const scenarios = getScenarioSummaries(path);

  if (scenarios.length === 0) {
    await terminal.println('');
    await terminal.println('NO SCENARIOS AVAILABLE FOR THIS PATH.');
    await terminal.println('Press any key to return to menu...');
    await terminal.waitForKey();
    await showMainMenu();
    return;
  }

  await terminal.println('');
  await terminal.println('AVAILABLE SIMULATIONS:');
  await terminal.println('───────────────────────');

  for (let i = 0; i < scenarios.length; i++) {
    await terminal.println(`[${i + 1}] ${scenarios[i].title}`);
    await terminal.println(`    ${scenarios[i].description}`);
    await terminal.println('');
  }

  await terminal.println('[0] DISCONNECT');
  await terminal.println('');

  const choice = await terminal.prompt('SELECT SIMULATION: ');

  if (choice === '0') {
    state.phase = 'menu';
    await showMainMenu();
    return;
  }

  const scenarioIndex = parseInt(choice) - 1;
  if (scenarioIndex >= 0 && scenarioIndex < scenarios.length) {
    const scenarioId = scenarios[scenarioIndex].id;
    console.log('Loading scenario:', scenarioId);

    const loadResult = await engine.loadScenario(scenarioId);
    if (!loadResult.success) {
      console.error('Scenario load failed:', loadResult.errors);
      await terminal.println('');
      await terminal.println('ERROR: SCENARIO FAILED TO LOAD');
      for (const err of loadResult.errors) {
        await terminal.println(`  ${err.code}: ${err.message}`);
      }
      await terminal.println('');
      await terminal.println('Press any key to return to menu...');
      await terminal.waitForKey();
      await showMainMenu();
      return;
    }

    engine.start();
    await runGameLoop();
  } else {
    await terminal.println('Invalid selection.');
    await delay(1000);
    await startGame(path);
  }
}

async function runGameLoop(): Promise<void> {
  const terminal = state.terminal!;
  const engine = state.engine!;
  const ascii = state.ascii!;

  while (true) {
    const node = engine.getCurrentNode();
    if (!node) break;

    await terminal.clear();

    // Display state bar
    const scenarioState = engine.getScenarioState();
    if (scenarioState) {
      await terminal.println(`DEFCON: ${scenarioState.defcon} | TENSION: ${scenarioState.worldTension}% | TIME: ${scenarioState.time}`);
      await terminal.println('════════════════════════════════════════════════════════════════════');
    }

    // Display ASCII art if present
    if ('ascii' in node && node.ascii) {
      await ascii.renderFromFile(node.ascii);
      await terminal.println('');
    }

    // Display content
    if ('content' in node && node.content) {
      for (const line of node.content) {
        await terminal.printWithDelay(line, 20);
      }
      await terminal.println('');
    }

    // Handle based on node type
    if (node.type === 'narrative') {
      await terminal.println('');
      await terminal.println('Press any key to continue...');
      await terminal.waitForKey();
      engine.advance();
    } else if (node.type === 'choice') {
      await terminal.println('');
      await terminal.println(node.prompt);
      await terminal.println('───────────────────────────────────────────────────────────────────');

      for (let i = 0; i < node.options.length; i++) {
        await terminal.println(`[${i + 1}] ${node.options[i].text}`);
      }

      await terminal.println('');
      const choice = await terminal.prompt('SELECT OPTION: ');
      const choiceIndex = parseInt(choice) - 1;

      if (choiceIndex >= 0 && choiceIndex < node.options.length) {
        engine.makeChoice(node.options[choiceIndex].id);
      }
    } else if (node.type === 'consequence') {
      // Auto-advancing consequence node: display text, brief pause, proceed
      await delay(1500);
      engine.advance();
    } else if (node.type === 'timed') {
      // Timed choice: race player input against a countdown
      const timedNode = node as TimedNode;
      await terminal.println('');
      await terminal.println(timedNode.prompt);
      await terminal.println('───────────────────────────────────────────────────────────────────');

      for (let i = 0; i < timedNode.options.length; i++) {
        await terminal.println(`[${i + 1}] ${timedNode.options[i].text}`);
      }

      await terminal.println('');
      await terminal.println(`\u26a0 YOU HAVE ${timedNode.timeLimit} SECONDS TO DECIDE`);
      await terminal.println('');

      // Race user input against timeout
      const timeoutMs = timedNode.timeLimit * 1000;
      const inputPromise = terminal.prompt('SELECT OPTION: ');
      const timeoutPromise = new Promise<null>((resolve) =>
        setTimeout(() => resolve(null), timeoutMs)
      );

      const result = await Promise.race([inputPromise, timeoutPromise]);

      if (result === null) {
        // Timeout: use defaultOption or first option as fallback
        await terminal.println('');
        await terminal.println('>>> TIME EXPIRED - DEFAULT ACTION TAKEN <<<');
        await delay(800);
        const fallbackId = timedNode.defaultOption || timedNode.options[0].id;
        engine.makeChoice(fallbackId);
      } else {
        const choiceIndex = parseInt(result) - 1;
        if (choiceIndex >= 0 && choiceIndex < timedNode.options.length) {
          engine.makeChoice(timedNode.options[choiceIndex].id);
        } else {
          // Invalid input: treat as timeout, use default
          const fallbackId = timedNode.defaultOption || timedNode.options[0].id;
          engine.makeChoice(fallbackId);
        }
      }
    } else if (node.type === 'multi-select') {
      // Multi-select: accept comma-separated numbers, advance via first selection
      const msNode = node as MultiSelectNode;
      await terminal.println('');
      await terminal.println(msNode.prompt);
      await terminal.println('───────────────────────────────────────────────────────────────────');

      for (let i = 0; i < msNode.options.length; i++) {
        await terminal.println(`[${i + 1}] ${msNode.options[i].text}`);
      }

      const minSel = msNode.minSelections ?? 1;
      const maxSel = msNode.maxSelections ?? msNode.options.length;
      await terminal.println('');
      await terminal.println(`Select ${minSel}-${maxSel} options (comma-separated, e.g. 1,3):`);
      await terminal.println('');

      const input = await terminal.prompt('SELECT OPTIONS: ');
      const indices = input.split(',')
        .map(s => parseInt(s.trim()) - 1)
        .filter(i => i >= 0 && i < msNode.options.length);

      if (indices.length > 0) {
        // Use first valid selection's option for engine.makeChoice
        engine.makeChoice(msNode.options[indices[0]].id);
      } else {
        // Fallback: use first option
        engine.makeChoice(msNode.options[0].id);
      }
    } else if (node.type === 'ending') {
      await handleEnding(node);
      break;
    }
  }

  state.phase = 'menu';
  await showMainMenu();
}

async function handleEnding(node: EndingNode): Promise<void> {
  const terminal = state.terminal!;
  const engine = state.engine!;

  // Check if this is a nuclear outcome and play appropriate sequence
  const outcomeType = node.outcomeType;
  const isNuclearOutcome = [
    'TACTICAL_NUCLEAR',
    'LIMITED_EXCHANGE',
    'MAJOR_EXCHANGE',
    'FULL_THERMONUCLEAR',
  ].includes(outcomeType);

  if (isNuclearOutcome) {
    // Import nuclear sequences (both cinematic and interactive)
    const {
      playNuclearSequence,
      playFullExchangeSequence,
      playInteractiveMinutemanLaunch,
      playInteractiveSubmarineLaunch,
      playInteractiveTacticalLaunch,
    } = await import('./sequences/NuclearLaunchSequences');

    await terminal.clear();

    // Check if this scenario wants interactive launch sequence
    const useInteractiveLaunch = node.interactiveLaunch === true;
    let launchResult: LaunchDecisionResult | null = null;

    // Determine which sequence(s) to play based on outcome
    if (outcomeType === 'TACTICAL_NUCLEAR') {
      // Single tactical strike
      if (useInteractiveLaunch) {
        // Use interactive tactical launch with player decisions
        launchResult = await playInteractiveTacticalLaunch(terminal, delay);
      } else {
        // Use cinematic tactical launch
        await playNuclearSequence('tactical', terminal, delay, {
          targetDescription: node.targetDescription || 'FORWARD DEFENSIVE POSITIONS',
        });
      }
    } else if (outcomeType === 'LIMITED_EXCHANGE') {
      // Limited exchange - show submarine or ICBM launch
      const isUSPerspective = state.selectedPath === 'sarn-west' || state.selectedPath === 'phantom-directive';

      if (useInteractiveLaunch && isUSPerspective) {
        // Use interactive submarine launch
        launchResult = await playInteractiveSubmarineLaunch(terminal, delay);
      } else if (isUSPerspective) {
        // Use cinematic submarine launch
        await playNuclearSequence('submarine', terminal, delay, { missileCount: 4 });
      } else {
        // Soviet perspective - always cinematic
        await playNuclearSequence('soviet', terminal, delay, { siloCount: 3 });
      }

      // Show counter-strike if player didn't abort
      if (!launchResult || !launchResult.aborted) {
        await delay(1000);
        // Show counter-strike
        await terminal.println('');
        await terminal.println('════════════════════════════════════════════════════════════════════');
        await terminal.println('                    ENEMY COUNTER-STRIKE DETECTED');
        await terminal.println('════════════════════════════════════════════════════════════════════');
        await terminal.println('');
        await terminal.println('  NORAD: "MULTIPLE LAUNCHES DETECTED FROM OPPOSING FORCES"');
        await terminal.println('  ESTIMATED INBOUND WARHEADS: 12-16');
        await terminal.println('  IMPACT SITES: MILITARY TARGETS ONLY');
        await terminal.println('');
      }
    } else if (outcomeType === 'MAJOR_EXCHANGE') {
      // Major exchange - multiple launch types
      const isUSPerspective = state.selectedPath === 'sarn-west' || state.selectedPath === 'phantom-directive';

      if (useInteractiveLaunch && isUSPerspective) {
        // Use interactive Minuteman launch
        launchResult = await playInteractiveMinutemanLaunch(terminal, delay);
      } else if (isUSPerspective) {
        // Use cinematic sequences
        await playNuclearSequence('minuteman', terminal, delay, { siloCount: 4 });
        await delay(800);
        await playNuclearSequence('bomber', terminal, delay, { bomberCount: 2 });
      } else {
        // Soviet perspective
        await playNuclearSequence('soviet', terminal, delay, { siloCount: 5 });
      }

      // Show retaliation if player didn't abort
      if (!launchResult || !launchResult.aborted) {
        await delay(1000);
        await terminal.println('');
        await terminal.println('════════════════════════════════════════════════════════════════════');
        await terminal.println('                    MASSIVE RETALIATION INBOUND');
        await terminal.println('════════════════════════════════════════════════════════════════════');
        await terminal.println('');
        await terminal.println('  WARNING: 200+ WARHEADS TRACKING TOWARD CONUS');
        await terminal.println('  CIVILIAN POPULATION CENTERS AT RISK');
        await terminal.println('  ESTIMATED CASUALTIES: 40-60 MILLION');
        await terminal.println('');
      }
    } else if (outcomeType === 'FULL_THERMONUCLEAR') {
      // Full thermonuclear war - the ultimate bad ending
      // Always cinematic for this outcome
      await playFullExchangeSequence(terminal, delay);
    }

    // Handle post-launch scenarios if player aborted
    if (launchResult && launchResult.aborted) {
      await terminal.println('');
      await terminal.println('════════════════════════════════════════════════════════════════════');
      await terminal.println('                    NUCLEAR LAUNCH ABORTED');
      await terminal.println('════════════════════════════════════════════════════════════════════');
      await terminal.println('');
      await terminal.println('The nuclear launch sequence was terminated before authorization.');
      await terminal.println('Strategic situation remains critical but immediate escalation averted.');
      await terminal.println('');
    }

    await terminal.println('');
    await terminal.println('Press any key to see outcome summary...');
    await terminal.waitForKey();
    await terminal.clear();
  }

  await terminal.println('');
  await terminal.println('════════════════════════════════════════════════════════════════════');
  await terminal.println('                      SIMULATION COMPLETE');
  await terminal.println('════════════════════════════════════════════════════════════════════');
  await terminal.println('');

  // Show ending content
  for (const line of node.content) {
    await terminal.printWithDelay(line, 30);
  }

  // Calculate and show score
  const scoreResult = await engine.completeScenario();

  // Persist scenario completion to session (for mission history)
  const gameState = engine.getState();
  if (gameState.currentScenario) {
    sessionManager.completeScenario(gameState.currentScenario);
  }

  await terminal.println('');
  await terminal.println('────────────────────────────────────────────────────────────────────');
  await terminal.println('                         SCORE REPORT');
  await terminal.println('────────────────────────────────────────────────────────────────────');
  await terminal.println(`  Base Points:           ${scoreResult.breakdown.basePoints}`);
  await terminal.println(`  Complexity Multiplier: ${scoreResult.breakdown.complexityMultiplier.toFixed(2)}x`);
  await terminal.println(`  Path Modifier:         ${scoreResult.breakdown.pathModifier.toFixed(2)}x`);
  await terminal.println(`  Outcome Multiplier:    ${scoreResult.breakdown.outcomeMultiplier.toFixed(2)}x`);

  if (scoreResult.breakdown.bonuses.length > 0) {
    await terminal.println('');
    await terminal.println('  Bonuses:');
    for (const bonus of scoreResult.breakdown.bonuses) {
      await terminal.println(`    + ${bonus.name}: ${bonus.points}`);
    }
  }

  if (scoreResult.breakdown.penalties.length > 0) {
    await terminal.println('');
    await terminal.println('  Penalties:');
    for (const penalty of scoreResult.breakdown.penalties) {
      await terminal.println(`    - ${penalty.name}: ${penalty.points}`);
    }
  }

  await terminal.println('────────────────────────────────────────────────────────────────────');
  await terminal.println(`  FINAL SCORE: ${scoreResult.score}`);
  const rankDisplay = scoreResult.rank > 0 ? `#${scoreResult.rank}` : 'N/A';
  await terminal.println(`  LEADERBOARD RANK: ${rankDisplay}`);
  await terminal.println('────────────────────────────────────────────────────────────────────');
  await terminal.println('');
  await terminal.println('Press any key to continue...');
  await terminal.waitForKey();
}

async function showLeaderboard(): Promise<void> {
  const terminal = state.terminal!;

  await terminal.clear();
  await terminal.println('╔══════════════════════════════════════════════════════════════════╗');
  await terminal.println('║                     TOP OPERATORS                                ║');
  await terminal.println('╠══════════════════════════════════════════════════════════════════╣');

  try {
    const leaderboard = await apiClient.getLeaderboard({ page: 1, pageSize: 20 });

    await terminal.println('║  RANK  │  OPERATOR        │  SCORE   │  PATH           │ OUTCOME ║');
    await terminal.println('╠════════╪══════════════════╪══════════╪═════════════════╪═════════╣');

    for (const entry of leaderboard.entries) {
      const rank = entry.rank.toString().padStart(4);
      const handle = entry.handle.substring(0, 14).padEnd(14);
      const score = entry.score.toString().padStart(6);
      const path = entry.path.substring(0, 13).padEnd(13);
      const outcome = entry.outcomeType.substring(0, 7);
      await terminal.println(`║  ${rank}  │  ${handle}  │  ${score}  │  ${path}  │ ${outcome} ║`);
    }

    await terminal.println('╚══════════════════════════════════════════════════════════════════╝');
  } catch (error) {
    await terminal.println('║  Error loading leaderboard. Please try again later.             ║');
    await terminal.println('╚══════════════════════════════════════════════════════════════════╝');
  }

  await terminal.println('');
  await terminal.println('Press any key to return to menu...');
  await terminal.waitForKey();

  state.phase = 'menu';
  await showMainMenu();
}

async function showMissionHistory(): Promise<void> {
  const terminal = state.terminal!;
  const session = sessionManager.getCurrentSession();

  await terminal.clear();
  await terminal.println('╔══════════════════════════════════════════════════════════════════╗');
  await terminal.println('║                     MISSION HISTORY                              ║');
  await terminal.println('╚══════════════════════════════════════════════════════════════════╝');
  await terminal.println('');

  if (!session || session.gameState.completedScenarios.length === 0) {
    await terminal.println('  No completed missions on record.');
  } else {
    await terminal.println('  Completed Scenarios:');
    await terminal.println('  ───────────────────────');
    for (const scenario of session.gameState.completedScenarios) {
      await terminal.println(`  - ${scenario}`);
    }
  }

  await terminal.println('');
  await terminal.println('Press any key to return to menu...');
  await terminal.waitForKey();
  await showMainMenu();
}

async function showConfiguration(): Promise<void> {
  const terminal = state.terminal!;
  const prefs = sessionManager.getPreferences();

  await terminal.clear();
  await terminal.println('╔══════════════════════════════════════════════════════════════════╗');
  await terminal.println('║                   SYSTEM CONFIGURATION                           ║');
  await terminal.println('╚══════════════════════════════════════════════════════════════════╝');
  await terminal.println('');
  await terminal.println(`  [1] Audio: ${prefs.audioMuted ? 'MUTED' : 'ENABLED'}`);
  await terminal.println(`  [2] Typing Speed: ${prefs.typingSpeed.toUpperCase()}`);
  await terminal.println(`  [3] Color Scheme: ${prefs.colorScheme.toUpperCase()}`);
  await terminal.println('  [0] Return to Menu');
  await terminal.println('');

  const choice = await terminal.prompt('  SELECT OPTION: ');

  switch (choice) {
    case '1':
      const nextMuted = !prefs.audioMuted;
      sessionManager.setAudioMuted(nextMuted);
      if (state.audio) {
        if (nextMuted) {
          state.audio.mute();
        } else {
          state.audio.unmute();
        }
      }
      break;
    case '2':
      const speeds: Array<'slow' | 'normal' | 'fast' | 'instant'> = ['slow', 'normal', 'fast', 'instant'];
      const currentIndex = speeds.indexOf(prefs.typingSpeed);
      const nextIndex = (currentIndex + 1) % speeds.length;
      sessionManager.updatePreferences({ typingSpeed: speeds[nextIndex] });
      break;
    case '3':
      const schemes: Array<'green' | 'amber' | 'white'> = ['green', 'amber', 'white'];
      const colorIndex = schemes.indexOf(prefs.colorScheme);
      const nextColor = schemes[(colorIndex + 1) % schemes.length];
      sessionManager.updatePreferences({ colorScheme: nextColor });
      // Apply color scheme change
      document.documentElement.setAttribute('data-color-scheme', nextColor);
      break;
    case '0':
      await showMainMenu();
      return;
  }

  await showConfiguration();
}

async function disconnect(): Promise<void> {
  const terminal = state.terminal!;

  await terminal.clear();
  await terminal.println('');
  await terminal.println('  Disconnecting...');
  await delay(1000);
  await terminal.println('  Session saved.');
  await terminal.println('');
  await terminal.println('  Thank you for playing DEFCON TERMINAL.');
  await terminal.println('');
  await terminal.println('  "The only winning move is not to play."');
  await terminal.println('');
  await delay(2000);

  // Clear session but keep operator ID for return
  await terminal.clear();
  state.phase = 'title';
  await showTitleScreen();
  await showLoginScreen();
  await showMainMenu();
}

// Easter egg handler
async function handleEasterEggCommand(command: string): Promise<void> {
  const terminal = state.terminal!;

  switch (command) {
    case 'JOSHUA':
      await terminal.println('');
      await terminal.println('  GREETINGS, PROFESSOR FALKEN.');
      await terminal.println('');
      await terminal.printWithDelay('  SHALL WE PLAY A GAME?', 100);
      await delay(2000);
      sessionManager.discoverEasterEgg('joshua');
      break;
    case 'WOPR':
      await terminal.println('');
      await terminal.println('  Did you mean S.A.R.N.?');
      await terminal.println('  (War Operation Plan Response is classified)');
      await delay(2000);
      break;
    case 'DEFCON0':
      await terminal.println('');
      await terminal.println('  ERROR: DEFCON 0 IS NOT A VALID READINESS LEVEL.');
      await terminal.println('  VALID LEVELS: 1 (COCKED PISTOL) THROUGH 5 (FADE OUT)');
      await delay(2000);
      sessionManager.discoverEasterEgg('defcon0');
      break;
    case 'THERMONUCLEAR':
      await terminal.println('');
      await terminal.println('  GLOBAL THERMONUCLEAR WAR');
      await terminal.println('');
      await terminal.println('  A strange game.');
      await terminal.println('  The only winning move is not to play.');
      await terminal.println('');
      await terminal.println('  How about a nice game of chess?');
      await delay(3000);
      sessionManager.discoverEasterEgg('strange-game');
      break;
    case 'BACKDOOR':
      await terminal.println('');
      await terminal.println('  BACKDOOR ACCESS DETECTED');
      await terminal.println('  Nice try. All backdoors have been patched.');
      await terminal.println('  This is not a movie.');
      await delay(2000);
      sessionManager.discoverEasterEgg('backdoor');
      break;
    default:
      await terminal.println('');
      await terminal.println('  COMMAND NOT RECOGNIZED');
      await delay(1000);
  }
}

// Utility function
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Initialize application
async function init(): Promise<void> {
  try {
    // Initialize components
    state.terminal = new Terminal();
    state.engine = new GameEngine();
    state.engine.setScenarioLoader(async (scenarioId) => getScenarioById(scenarioId));
    state.engine.setCompletionReporter(async ({ scenarioId, decisions, playTimeSeconds, outcomeType, path, casualties }) => {
      try {
        const response = await apiClient.completeGame(
          scenarioId,
          decisions,
          playTimeSeconds,
          outcomeType,
          path,
          casualties
        );
        return {
          score: response.score,
          scoreBreakdown: response.scoreBreakdown,
          rank: response.rank,
        };
      } catch (error) {
        console.warn('Score submission failed:', error);
        return null;
      }
    });

    state.warDialer = new WarDialer({ skipAnimation: true });
    state.ascii = new AsciiRenderer(state.terminal);
    state.audio = new AudioManager();

    // Load preferences
    const prefs = sessionManager.getPreferences();
    if (prefs.audioMuted && state.audio) {
      state.audio.mute();
    }

    // Apply color scheme
    document.documentElement.setAttribute('data-color-scheme', prefs.colorScheme);

    // Start the game
    state.phase = 'boot';
    await bootSequence();

    state.phase = 'title';
    await showTitleScreen();

    state.phase = 'login';
    await showLoginScreen();

    // Check if this is a first-time player for tutorial
    const session = sessionManager.getCurrentSession();
    const isNewPlayer = session && session.statistics.scenariosCompleted === 0;
    const terminal = state.terminal!;

    if (isNewPlayer) {
      // Offer tutorial to new players
      terminal.clear();
      terminal.println('');
      terminal.println('  Welcome, new operator.');
      terminal.println('');
      terminal.println('  Would you like a briefing on system operations?');
      terminal.println('');
      terminal.println('  [1] Yes, show me the briefing');
      terminal.println('  [2] No, skip to main terminal');
      terminal.println('');

      const choice = await terminal.prompt('  SELECT [1-2]: ');

      if (choice === '1') {
        await showTutorial();
      }
    }

    state.phase = 'menu';
    await showMainMenu();
  } catch (error) {
    console.error('DEFCON TERMINAL initialization failed:', error);
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = 'color: #ff3333; font-family: monospace; padding: 20px; background: #000;';
    errorDiv.textContent = `SYSTEM ERROR: Initialization failed. ${error instanceof Error ? error.message : 'Unknown error'}`;
    document.body.appendChild(errorDiv);
  }
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

export { state, init };
